import type {
  ImageBuffer,
  ImageReference,
  OperationContract,
  RenderRequest,
  RenderResponse,
  ResourceContract,
  Result,
  SampleDataRef,
  SessionContext,
} from '@tsagko/label-contracts'
import { err, ok, SessionContextSchema } from '@tsagko/label-contracts'
import { createLocalOperations } from '../shared/local-operations'
import type { EmbeddedTransport } from './transport'

/**
 * Embedded `OperationContract`.
 *   - `generateZpl` / `lintTemplate` / `evaluateRules`: run LOCALLY via the
 *     shared `createLocalOperations` — identical logic/output to the Demo
 *     adapter, just backed by the transport-marshaled `resources` for
 *     host-asset image lookups.
 *   - `render` / `resolveImage` / `sampleData`: proxied verbatim to the
 *     host via `transport.request`; the host's `Result` envelope is
 *     passed through unchanged (interface sheet: "Result payloads are
 *     already Result envelopes from the host").
 *   - `getSessionContext`: proxied, then zod-validated against
 *     `SessionContextSchema` before being handed back — this is the one
 *     method this layer does NOT blindly pass through, since a malformed
 *     session context must never silently reach the designer's mount
 *     logic. Host-side error Results are returned unchanged without being
 *     run through zod at all.
 */
export function createOperationContract(
  transport: EmbeddedTransport,
  resources: ResourceContract
): OperationContract {
  const local = createLocalOperations(resources)
  return {
    ...local,
    render: (request: RenderRequest, signal?: AbortSignal): Promise<Result<RenderResponse>> =>
      transport.request<RenderResponse>('render', { request }, { signal }),
    resolveImage: (ref: ImageReference, signal?: AbortSignal): Promise<Result<ImageBuffer>> =>
      transport.request<ImageBuffer>('resolveImage', { ref }, { signal }),
    sampleData: (
      ref: SampleDataRef,
      signal?: AbortSignal
    ): Promise<Result<Record<string, unknown>>> =>
      transport.request<Record<string, unknown>>('sampleData', { ref }, { signal }),
    async getSessionContext(signal?: AbortSignal): Promise<Result<SessionContext>> {
      const result = await transport.request<unknown>('getSessionContext', {}, { signal })
      if (result.status === 'error') return result
      const parsed = SessionContextSchema.safeParse(result.data)
      if (!parsed.success) {
        return err(
          'INTERNAL',
          `Host getSessionContext payload failed validation: ${parsed.error.message}`,
          { recoverable: false }
        )
      }
      return ok(parsed.data, { etag: result.etag, cursor: result.cursor })
    },
  }
}
